import Button from "../Button/Button";
import { Box, Typography } from "@mui/material";
import trans from "helpers/trans";
import PropTypes from "prop-types";
import { IGPTablePagination } from "./Table";
import TextField from "../Form/TextField";
import { IFormChangeResponse } from "ui/Form/Form";
import { useEffect, useState } from "react";

interface IGPTablePaginationProps {
    pagination: IGPTablePagination;
    rows: number;
    tableUpdate?: Function;
    showTotal?: boolean;
    showPage?: boolean;
    showNextButton?: boolean;
    showPrevButton?: boolean;
    noMargin?: boolean;
}

function GPTablePagination({
    pagination,
    rows,
    tableUpdate,
    showTotal = true,
    showPage = true,
    showNextButton = true,
    showPrevButton = true,
    noMargin = false,
}: IGPTablePaginationProps) {
    const [page, setPage] = useState(pagination.page);

    useEffect(() => {
        setPage(pagination.page);
    }, [pagination.page]);

    if (rows === 0 || !tableUpdate) {
        return null;
    }

    const nextPage = () => {
        if (!pagination?.isLastPage) {
            tableUpdate("page", pagination.page + 1);
        }
    };

    const prevPage = () => {
        if (pagination.page > 1) {
            tableUpdate("page", pagination.page - 1);
        }
    };

    return (
        <Box
            sx={{
                alignItems: "center",
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                mb: noMargin ? 0 : 2,
                mt: noMargin ? 0 : 1,
            }}
        >
            <Typography sx={{ display: "flex", gap: 2 }}>
                {showTotal && (
                    <Box>
                        <strong>{pagination.total}</strong>{" "}
                        {pagination.total && pagination.total > 1
                            ? trans("ui.table.tablePagination.results")
                            : trans("ui.table.tablePagination.result")}
                    </Box>
                )}
                {showPage && (
                    <Box>
                        <TextField
                            id="page"
                            label="Page"
                            value={page}
                            onChange={({ value }: IFormChangeResponse) => {
                                setPage(value);
                            }}
                            onBlur={() => {
                                if (page < 1) {
                                    setPage(1);
                                    tableUpdate("page", 1);
                                } else if (
                                    page >
                                    Math.ceil(
                                        pagination.total / pagination.per_page,
                                    )
                                ) {
                                    const lastPage = Math.ceil(
                                        pagination.total / pagination.per_page,
                                    );
                                    setPage(lastPage);
                                    tableUpdate("page", lastPage);
                                } else {
                                    tableUpdate("page", page);
                                }
                            }}
                            size="small"
                            sx={{
                                width: 50,
                                ".MuiInputBase-input": {
                                    textAlign: "center",
                                    p: 0,
                                },
                            }}
                        />{" "}
                        /{" "}
                        <strong>
                            {Math.ceil(pagination.total / pagination.per_page)}
                        </strong>
                    </Box>
                )}
            </Typography>

            <Box>
                {showPrevButton && (
                    <Button
                        color="gray2"
                        disabled={pagination.page === 1}
                        onClick={prevPage}
                        size="small"
                        sx={{ mr: 1 }}
                        variant="outlined"
                    >
                        {trans("ui.table.tablePagination.previous")}
                    </Button>
                )}
                {showNextButton && (
                    <Button
                        color="gray2"
                        disabled={pagination.isLastPage}
                        onClick={nextPage}
                        size="small"
                        variant="outlined"
                    >
                        {trans("ui.table.tablePagination.next")}
                    </Button>
                )}
            </Box>
        </Box>
    );
}

GPTablePagination.propTypes = {
    tableUpdate: PropTypes.func,
};

GPTablePagination.defaultProps = {
    tableUpdate: () => null,
};

export default GPTablePagination;
